import { SyntheticEvent, useEffect, useMemo } from "react";
import { FormControl, IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { PageCallback } from "react-pdf/dist/cjs/shared/types.d.ts";
import { UseZoomControls } from "./useZoomControls";
import { PresetDocumentZoom } from "redux/slices/session";
const DEFAULT_SCALE = 1.0;

export default function ZoomControls(props: {
  isImage: boolean;
  zoomControls: UseZoomControls;
  rotateCCW: () => void;
  pageProxy: PageCallback | undefined;
  intakeId: number;
  docViewerSize: { width: number; height: number };
}) {
  const { pageProxy, intakeId, docViewerSize, zoomControls } = props;
  const { scale, option, setScale, magnified, canZoomIn, canZoomOut, zoomIn, zoomOut } = zoomControls;
  const landscape = useMemo(() => {
    return pageProxy ? pageProxy.rotate === 90 || pageProxy.rotate === 270 : false;
  }, [pageProxy]);
  const pageWidthScale = useMemo(() => {
    const view2 = pageProxy?.view[2];
    const view3 = pageProxy?.view[3];
    if (docViewerSize.width && view2 && view3) {
      return ((docViewerSize.width / (!landscape ? view2 : view3)) * DEFAULT_SCALE) / 1;
    } else {
      return 1;
    }
  }, [pageProxy, docViewerSize.width, landscape]);

  const pageHeightScale = useMemo(() => {
    const view2 = pageProxy?.view[2];
    const view3 = pageProxy?.view[3];
    if (docViewerSize.height && view2 && view3) {
      return (docViewerSize.height / (!landscape ? view3 : view2)) * DEFAULT_SCALE;
    } else {
      return 1;
    }
  }, [pageProxy, docViewerSize.height, landscape]);
  const pageFit = useMemo(() => {
    return Math.min(pageWidthScale, pageHeightScale);
  }, [pageWidthScale, pageHeightScale]);
  const horizontalScale = useMemo(() => {
    return landscape ? pageWidthScale : Math.min(pageHeightScale, pageWidthScale);
  }, [pageWidthScale, pageHeightScale, landscape]);
  const pageAuto = useMemo(() => {
    return Math.min(1.25, horizontalScale);
  }, [horizontalScale]);

  const getScaleForOption = (option: PresetDocumentZoom): number => {
    let newScale: number;

    switch (option) {
      case "auto":
        newScale = pageAuto;
        break;
      case "page-fit":
        newScale = pageFit;
        break;
      case "page-width":
        newScale = pageWidthScale;
        break;
      default:
        newScale = parseFloat(option);
        break;
    }

    return newScale;
  };

  const onZoomSelect = (event: SyntheticEvent) => {
    const option = event.currentTarget.getAttribute("data-value") as PresetDocumentZoom;
    const newScale = getScaleForOption(option);

    zoomControls.onZoomSelect(option, newScale);
  };

  useEffect(() => {
    if (!magnified) {
      if (option === "page-fit") {
        setScale(pageFit);
      } else if (option === "page-width") {
        setScale(pageWidthScale);
      }
    }
  }, [pageWidthScale, option, pageFit, magnified]);

  useEffect(() => {
    const optionToUse = zoomControls.savedZoom.option ?? "page-width";
    const newScale = zoomControls.savedZoom.scale ?? getScaleForOption(optionToUse);
    zoomControls.onZoomSelect(optionToUse, newScale);
  }, [intakeId]);

  return (
    <div className="flex items-center pl-1">
      <FormControl variant="standard" sx={{ ml: 1, minWidth: 58 }} data-pendo="zoom-controls-select">
        <Select value={option} renderValue={() => `${Math.floor(scale * 100)}%`}>
          <MenuItem onClick={onZoomSelect} value="auto">
            Auto
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="page-fit">
            Page Fit
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="page-width">
            Page Width
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="0.1">
            10%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="0.25">
            25%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="0.5">
            50%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="1">
            100%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="1.25">
            125%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="1.5">
            150%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="2">
            200%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="4">
            400%
          </MenuItem>
          <MenuItem onClick={onZoomSelect} value="8">
            800%
          </MenuItem>
        </Select>
      </FormControl>
      <Tooltip title="Zoom Out" arrow>
        <span>
          <IconButton data-pendo="zoom-controls-zoom-out" disabled={!canZoomOut} onClick={zoomOut}>
            <Remove />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Zoom In" arrow>
        <span>
          <IconButton data-pendo="zoom-controls-zoom-in" disabled={!canZoomIn} onClick={zoomIn}>
            <Add />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
